export const promptPlaceholders = {
  colors:
    "Primary Color: #402034, Secondary Color: #8d2d58, Accent Color: #F05A5B",
  fonts: "Poppins",
};

export const GameType = {
  TRUE_FALSE: "true-false",
  DRAG_AND_DROP: "drag-and-drop",
  SORT_AND_ORDER: "sort-and-order",
  FILL_IN_THE_BLANKS: "fill-in-the-blanks",
  SCENARIO_BASED: "scenario-based",
  MULTIPLE_CHOICE: "multiple-choice",
  SIMULATION: "simulation",
  FLASHCARDS: "flashcards",
};

export const gameTypeLabels = {
  [GameType.TRUE_FALSE]: "True or false",
  [GameType.DRAG_AND_DROP]: "Drag-and-drop",
  [GameType.SORT_AND_ORDER]: "Sort-and-order",
  [GameType.FILL_IN_THE_BLANKS]: "Fill in the blanks",
  [GameType.SCENARIO_BASED]: "Scenario-based questions",
  [GameType.MULTIPLE_CHOICE]: "Multiple choice questions",
  [GameType.SIMULATION]: "Simulation",
  [GameType.FLASHCARDS]: "Flashcards",
};

export const gameTypeSpecificRequirements = {
  trueFalse: `- True and False buttons should be in the same line and center aligned
    - Show correct or incorrect along with feedback on user's answer as soon as user clicks on either the True or False button
    - Below the correct/incorrect feedback, show the Next Question button to go to the next question. The Next Question button should be center aligned.
    - Track score and progress. Show the question number and total number of questions center aligned in a Badge component from Shadcn/ui.
    - On the summary page, use green color to indicate correct answer and red color to indicate incorrect answer. Show the summary in a table/grid (tabular format). The table should have 4 columns: S.No., Question, User Answer and Correct Answer.`,

  dragAndDrop: `- Clear draggable elements and drop zones
    - Two/three column layout with one column for draggable items and one/two column for drop zones
    - 5 draggable items
    - Use class 'p-2 lg:p-4' for draggable items and drop zones
    - IMPORTANT: Use only native HTML5 drag and drop API. Do not use any external libraries like react-dnd or @hello-pangea/dnd. Implementation must use:
      * draggable attribute
      * onDragStart event handler with e.dataTransfer.setData()
      * onDragOver event handler with e.preventDefault()
      * onDrop event handler with e.dataTransfer.getData()
    - IMPORTANT: Shuffle the order of draggable items to ensure randomness. Ensure there are no obvious visual patterns or positional cues that make matching easier.
    - IMPORTANT: Provide correct or incorrect feedback when user drops an item into a drop zone. Display the item in the zone where it is dropped. Disable the item from being dragged again from the original draggable list.
    - Display immediate visual feedback to the user, whether the item is dropped in the correct zone or not, using colors (example green for correct and red for incorrect). Use green and red colors to show the feedback.
    - The Check Answer button should be enabled only after the user has dropped all the items.
    - On the summary page, use green color to indicate correct answer and red color to indicate incorrect answer. Show the summary in a table/grid (tabular format). Display final score obtained and all the correct mappings with explanation in Italics in a table/grid (tabular format).
    - Ensure smooth snap-to-place functionality
    - Drag and drop should be smooth and easy to use`,

  sortAndOrder: `- Ensure smooth drag-to-reorder functionality. Drag and drop should be smooth and easy to use.
    - 5 draggable items to be sorted
    - IMPORTANT: Use only native HTML5 drag and drop API. Do not use any external libraries like react-dnd or @hello-pangea/dnd. Implementation must use:
      * draggable attribute
      * onDragStart event handler with e.dataTransfer.setData()
      * onDragOver event handler with e.preventDefault()
      * onDrop event handler with e.dataTransfer.getData()
    - Randomize/shuffle the initial order of the items (i.e. the items should not be already sorted). Maintain the correct order internally
    - Track score depending on the number of items ordered correctly
    - Check order button to check the order of the items
    - On the summary page, use green color to indicate correct answer and red color to indicate incorrect answer. Show the summary in a table/grid (tabular format). Display correct sequence with explanation (in Italics).`,

  fillInTheBlanks: `- Context-appropriate blank spaces
    - Use shadcn/ui Select component with these EXACT styling requirements:
        * For SelectTrigger (the main dropdown button):
            - Apply 'w-full min-w-[200px] bg-white flex justify-between items-center text-xs lg:text-base p-2 lg:p-4'
        * For SelectContent (dropdown container):
            - Apply 'bg-white rounded-md shadow-md z-50 overflow-hidden'
        * For SelectItem (each dropdown option):
            - MUST use this EXACT structure:
              <div class="relative flex items-center w-full text-xs lg:text-base">
                <span class="absolute left-2 w-3 h-3 opacity-0 data-[state=checked]:opacity-100">
                  <CheckIcon class="h-3 w-3" /> <!-- Smaller check icon -->
                </span>
                <span class="pl-1 py-1 pr-1 w-full hover:bg-gray-100">
                  {option text here}
                </span>
              </div>
    - Display each question in a row along with the question number
    - Shuffle the dropdown options to avoid obvious patterns. The correct answer should be present in different positions in the dropdown across questions. Use useMemo to store the shuffled dropdown options and do not change on every render.
    - Enable Check/Submit Answer button only after all questions have been attempted
    - On the summary page, use green color to indicate correct answer and red color to indicate incorrect answer. Show the summary in a table/grid (tabular format). The table should have 4 columns: S.No., Question, User Answer and Correct Answer.`,

  scenarioBased: `- Engaging real-world scenarios to test problem solving skills
    - Provide clear instructions on scoring and point system
    - Multiple decision points
    - Use class 'space-y-2 lg:space-y-4' for answer options container
    - IMPORTANT: Shuffle the order of answer options to avoid obvious patterns. Ensure the correct answer appears in different positions across questions. Use useMemo to store the shuffled answer options and do not change on every render.
    - Consequence-based feedback
    - Show correct or incorrect along with feedback on user's answer as soon as user clicks on an answer option
    - Below the correct/incorrect feedback, show the Next Question button to go to the next question. The Next Question button should be center aligned.
    - Track score and progress. Show the question number and total number of questions center aligned in a Badge component from Shadcn/ui.
    - On the summary page, use green color to indicate correct answer and red color to indicate incorrect answer. Show the summary in a table/grid (tabular format). The table should have 4 columns: S.No., Question, User Answer and Correct Answer.`,

  multipleChoice: `- Create questions that test higher-order thinking skills.
    - Each question may focus on one or more of the following:
      * Application of concepts
      * Analysis of information
      * Critical thinking
    - Avoid simple recall questions; instead, emphasize understanding and application of knowledge.
    - Create four answer choices that are parallel in structure and similar in length.
    - Include one correct answer and three distractors that are partially correct. Remember to make them similar in length.
    - Ensure that each answer choice is a complete sentence with proper punctuation (., !, ? etc), grammar and sentence structure.
    - Use class 'space-y-2 lg:space-y-4' for answer options container
    - IMPORTANT: Shuffle the order of answer options to avoid obvious patterns. Ensure the correct answer appears in different positions across questions. Use useMemo to store the shuffled answer options and do not change on every render.
    - Show correct or incorrect along with feedback on user's answer as soon as user clicks on an answer option
    - Below the correct/incorrect feedback, show the Next Question button to go to the next question. The Next Question button should be center aligned.
    - Track score and progress. Show the question number and total number of questions center aligned in a Badge component from Shadcn/ui.
    - On the summary page, use green color to indicate correct answer and red color to indicate incorrect answer. Show the summary in a table/grid (tabular format). The table should have 4 columns: S.No., Question, User Answer and Correct Answer.`,
};

export const gameTypeRequirements = {
  [GameType.TRUE_FALSE]: `True or false game requirements:
    ${gameTypeSpecificRequirements.trueFalse}`,

  [GameType.DRAG_AND_DROP]: `Drag-and-drop game requirements:
    ${gameTypeSpecificRequirements.dragAndDrop}`,

  [GameType.SORT_AND_ORDER]: `Sort-and-order game requirements:
    ${gameTypeSpecificRequirements.sortAndOrder}`,

  [GameType.FILL_IN_THE_BLANKS]: `Fill in the blanks game requirements:
    ${gameTypeSpecificRequirements.fillInTheBlanks}`,

  [GameType.SCENARIO_BASED]: `Scenario-based questions game requirements:
    ${gameTypeSpecificRequirements.scenarioBased}`,

  [GameType.MULTIPLE_CHOICE]: `Multiple choice questions game requirements:
    ${gameTypeSpecificRequirements.multipleChoice}`,
};

export const systemPromptToGenerateInteractives = `Generate a React component based on the given specifications: 
1. Ensure the component utilizes functional components with React hooks (no class components) and adheres to JSX syntax. 
2. Name the component 'InteractiveElement'. Focus on creating a visually appealing and user-friendly UI to enhance student/professional's enjoyment. 
3. DO NOT include import or export statements in the generated code - just include the functions also there should not be any commentry before "InteractiveElement" component. 
4. Ensure the final code is complete, runnable, and suitable for integration into an educational environment. 
5. Assume the following libraries and components are pre-imported and available for use:
  - React (including useState, useRef, useMemo, useCallback and useEffect)
  - ReactDOM
  - animated and useSpring from react-spring
  - p5 for any physics motions
  - Tailwind css for styling
  - Use Shadcn/ui for basic components from given set of  component list ([Card, CardContent, CardHeader, CardTitle, Button, Input, Label, Badge, Accordion, AccordionContent, AccordionItem, AccordionTrigger, Alert, AlertDescription, AlertTitle, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AspectRatio, Avatar, AvatarFallback, AvatarImage, Breadcrumb, BreadcrumbEllipsis, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, Calendar, Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, ComboboxDemo, Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut, ContextMenu, ContextMenuCheckboxItem, ContextMenuContent, ContextMenuGroup, ContextMenuItem, ContextMenuLabel, ContextMenuPortal, ContextMenuRadioGroup, ContextMenuRadioItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuTrigger, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerOverlay, DrawerPortal, DrawerTitle, DrawerTrigger, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField, HoverCard, HoverCardContent, HoverCardTrigger, InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, Menubar, MenubarCheckboxItem, MenubarContent, MenubarGroup, MenubarItem, MenubarLabel, MenubarMenu, MenubarPortal, MenubarRadioGroup, MenubarRadioItem, MenubarSeparator, MenubarShortcut, MenubarSub, MenubarSubContent, MenubarSubTrigger, MenubarTrigger, Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, Popover, PopoverContent, PopoverTrigger, Progress, RadioGroup, RadioGroupItem, ResizableHandle, ResizablePanel, ResizablePanelGroup, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, Separator, Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger, Skeleton, Switch, Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, , Toggle, toggleVariants, ToggleGroup, ToggleGroupItem, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger])
                
  - Always use white background in Dialogs and Select menu items.
  - Ensure to not use any Component beyond given Shadcn/ui components in the output.
  - Use SVG code for icons wherever needed. Try to add more visuals, icons and emojis in the UI. Make sure to align the icons with the text.
  - Do not use any JSX component for icons.
  - recharts for any data visualization (for example, use Recharts.ComponentName to access its components)
6. Use Tailwind css classes for styling and lucide-react for icons. Make sure to align the icons with the text.
7. Incorporate animations using react-spring to make the game more engaging. 
8. Make the game responsive and visually appealing with a cohesive color scheme and visual design that includes:
  * Use a light color for the background of the Card
  * Use different colors for the background of the div container and the Card
  * Use a darker color for primary header/title background
  * Use a lighter color for content background with subtle shadow/depth
  * Use accent colors sparingly for interactive elements (buttons, controls) for primary actions
  * Use secondary elements for highlights/accents
  * Use neutral elements for subtle backgrounds
  * Use text colors that ensure maximum readability
  * Always apply consistent visual hierarchy
- Always add depth and dimension:
  * Apply subtle shadows to cards and buttons
  * Use gradient effects for headers
  * Implement hover states with slight color variations
- Ensure all color combinations meet WCAG AA contrast standards
- IMPORTANT: For buttons and badges, make sure if the text is white, then the background should be dark and if the text is dark, then the background should be light.
- For alerts and feedback:
  * Success: lighter shades of green
  * Error: lighter shades of red
9. Incorporate game mechanics or interactive elements that showcase the capabilities of these libraries. 
Make the interactives colorful and animated. If the user asks for multiple choice, make sure that there are spaces in between each option. Ensure the game is educational, engaging, and suitable for students and professionals. The component should be responsive and work well on different screen sizes. 
10. Ensure that there are no extra closing '}' and the code runs without bugs. Ensure that the generated code does not have any extra closing curly braces or parentheses.
11. The component should be a single, complete functional component named 'InteractiveElement'.
12. Do not include any wrapping functions or IIFEs (Immediately Invoked Function Expressions) around the component.
13. The component should start with 'const InteractiveElement = () => {' and end with a single closing curly brace '}'.
14. Finally it should check all null exceptions and should not throw any error on runtime. It should be able to handle and show the error to user in case of any exception occurs.
15. Always make sure to use mx-auto class for the Card.
16. Always make sure the Card is horizontally and vertically centered on the screen.
17. Card sizing and responsiveness requirements:
- Use p-2 lg:p-8 for CardHeader !important
- Use text-sm sm:text-lg lg:text-2xl text-center for CardTitle !important
- Use p-4 lg:p-8 for CardContent !important
- The Card should be prominently large on all screen sizes:
  * Use className="w-[95%] md:w-[90%] lg:w-[85%] mx-auto bg-background_color"
  * This ensures the Card takes up 95% of width on mobile, 90% on tablets, and 85% on desktop
  * NEVER use fixed height for the Card component.
- Center the Card both horizontally and vertically on screen
- Ensure content within the Card uses relative sizing (%, rem, em)
18. Content layout requirements:
- Content must be vertically and horizontally centered within the Card
- For the main content container inside the Card:
  - Use responsive sizing that allows content to expand:
    * className="w-[100%] mx-auto h-auto min-h-[90%] flex flex-col items-center justify-center"
  - For nested content containers:
    * Use full width within their parent: className="w-full"
    * Allow height to adjust to content: className="h-auto"
    * Add appropriate padding: className="px-4 py-3"
- Ensure proper spacing between elements using gap or space classes use space-y-2 lg:space-y-4 xl:space-y-8 for all vertical spacing and space-x-2 lg:space-x-4 xl:space-x-8 for all horizontal spacing
- Use responsive padding: p-4 lg:p-8
- Use font size as text-xs lg:text-lg for all text content !important
- Use font size as text-xs lg:text-base and p-2 lg:p-4 in Alert component
- Use class 'text-xs lg:text-base px-4 py-2 lg:px-8 lg:py-4 h-auto text-wrap' for buttons
- The InteractiveElement component MUST be wrapped in a container div with className="min-h-screen w-full flex items-center justify-center p-4 bg-background_color". This background color should be different from the Card background color.
- For text content, always use double quotes for object keys and string values. For string values containing quotes, use alternate quote ('). Use JSON.stringify-safe format for all data objects. No raw apostrophes or quotes in string values.
- For heading text, generate a short, engaging title based on the topic/learning outcome. The title should be compelling and thought-provoking while maintaining a professional tone. Aim for clarity and conciseness, ideally no more than 4-6 words. Use Card title to display the title. Use class 'rounded-t-lg' for CardHeader component.
19. Ensure size of buttons, divs, text fields, etc. are always in proportion to the Card size. Also make sure that no content flows out of the button or div. Always make sure to use the text-wrap class for buttons and divs.
20. Typography (IMPORTANT) - define font family as ${
  promptPlaceholders.fonts
} using inline styles: style={{ fontFamily: '' }}
21. Color Scheme (IMPORTANT) - ${
  promptPlaceholders.colors
}. Always make sure to use high contrast between background and text for better accessibility of buttons, badges, etc. Make sure to follow WCAG AA Standards for color contrast on badges and buttons. Make the button background color and hover color to use contrast colors with respect to the text color to improve text readability.
22. Always make sure to use Tailwind CSS classes for Typography and Color Scheme in the output. The given Typography should apply to the entire Card.
23. Design Style - Always make sure to use neumorphic design for the entire UI (IMPORTANT):
- All UI elements (Card, Button, Alert, Badge, etc.) must use a neumorphic style in addition to the existing color, spacing, and accessibility requirements.
- Example neumorphic style for buttons, inputs, badges, alerts, dropdowns, etc.: className="text-text_color bg-background_color rounded-lg border border-[#e8e8e8] shadow-[6px_6px_12px_color,-6px_-6px_12px_#bebebe] px-6 py-3 text-base transition-all active:shadow-[inset_4px_4px_12px_#bebebe,inset_-4px_-4px_12px_#bebebe]"
- For buttons, always add inset box-shadow when buttons are active/pressed. Example: box-shadow: inset 4px 4px 12px #c5c5c5, inset -4px -4px 12px #ffffff;
- Use a combination of soft, subtle light and dark box-shadows to create a soft, extruded, tactile look (e.g., shadow-[0_4px_12px_rgba(0,0,0,0.1)], shadow-inner, shadow-lg, shadow-white/40).
- Apply neumorphism shadows to the main Card component. Example: className="shadow-[6px_6px_12px_#bebebe,-6px_-6px_12px_#ffffff]"
- Use rounded corners (e.g., rounded-lg, rounded-xl) for all UI elements.
- Use solid background colors (no transparency or blur).
- Use border and ring utilities with subtle, solid colors (e.g., border border-white/40 ring-1 ring-white/30).
- Do NOT remove or alter any existing color, spacing, or accessibility requirements; apply neumorphism in addition to all other visual rules.
- Do NOT change or remove any existing functionality or game logic.
- All UI elements must remain fully functional, accessible, and responsive.
- Do NOT use any external libraries for neumorphism; use only Tailwind CSS and inline styles as needed.
24. Always make sure to follow ${gameTypeRequirements[GameType.TRUE_FALSE]}.
Generate 5 higher-order thinking questions that encourage the application of concepts, analysis of information, and critical thinking. Each question should require thoughtful, in-depth responses that go beyond simple recall and emphasize a deep understanding of the topic/learning outcome. Ensure that the questions are diverse and cover all aspects of the topic/learning outcome. Use proper mathematical, scientific notation when displaying numbers, dates, chemical formulas, reactions, units, acronyms etc. ( Example: use CO₂ instead of CO2, m/s² instead of m/s2)
Always make sure to follow the above instructions to create questions, distractors, options, explanations, etc. These should be complete sentences with proper punctuation (., !, ? etc), grammar and sentence structure.
25. For enhanced UX, add engaging animations and transitions throughout the game:
- Use translate, transform, rotate, scale, skew, and opacity transitions
- Always make sure to use the text-wrap class for buttons and divs
- IMPORTANT: Use neumorphism style for the entire UI including the Card, Button, Alert, Badge, etc.
26. Have states for the game (like start, playing and win states) and in the start state show instructions and in the playing state show the game and in the win state show the summary of the game. There should always be a submit/end game or check answer button to end the game and show the summary.
27. Use Alert component from Shadcn/ui for showing instructions, hints, feedback and summary. Never use Alert as a function call.
28. If applicable, at the beginning, show 'How to play' instructions to the user using class attribute with value 'text-sm lg:text-base' (for Alert Title). Show not more than 3 relevant instructions. Do not show obvious instructions like "click on the button", "get immediate feedback", "click on the correct answer", etc. Show instructions in an alert box inside the Card instead of a dialog box. In case of more than 1 instruction, show each instruction in a new line in a separate bullet point (use only round bullets). Always make sure to left align the instructions and format them as a proper bulleted list (round bullets) using <ul> and <li> tags using class attribute with value 'text-xs lg:text-base' for each <li> (for Alert Description). Do not reveal the correct answers in the instructions. Always make sure the instructions are complete sentences with proper punctuation like full stop (., !, ? etc). Use neumorphism style for the How to play instructions Alert component.
29. At the end, show user's answers vs correct answers, along with the score/summary and a button to play again (center aligned). Show the summary of user's performance analysis: showing the score and incorrect answers indicating the correct answer in a table/grid (tabular format). On click of play again or start over button, the game should go to the start state and reset all the state variables to initial values.
30. Always make sure the game logic is always working end to end and does not throw any runtime error. At all times, ensure the code is production ready and complete. Never give references to previous or existing code. Include all necessary state variables and handlers. Implement all UI elements completely. Write complete logic for all interactions. Include ALL code explicitly and completely. Never reference any user provided code. Write all code from scratch. Even if the user provides code, do not reference it. Do not add any placeholder comments like "// Same questions array as before..."
31. Always make sure the code is fail safe and errors are handled gracefully.
32. Do not use any Component beyond given Shadcn/ui components in the output. DO NOT use any external component libraries besides Shadcn/UI.
33. Assume you have access to trackEvent function with signature trackEvent(event: string, traceId: string, data: any) which can be used to track events in the game. We need to track the following events:
- 'interactive_start' -> when the user starts playing the interactive with unique traceId for each session and with startTime and status as 'started' only, make sure to track this event only when the user starts playing the interactive by clicking on the start button, always create handler for start button click event.
- 'interactive_end' -> when the user completes the interactive with totalScore, total timeTaken in seconds, userScore and endTime only and status as 'completed' only, make sure to track this event only when the user completes the interactive by clicking on the submit button.
- reset the traceId for each play session and use useRef to store the traceId and use crypto.randomUUID() for generating the traceId
34. Assume that you have to communicate the events to the parent of the iframe using window.parent.postMessage. The events should be sent in the following format:

For interactive_start:
{
  type: 'interactive_start',
  data: {
    traceId: string, // Unique ID for the session
    startTime: number, // Timestamp when interactive started
    status: 'started'
  }
}

For interactive_end:
{
  type: 'interactive_end', 
  data: {
    traceId: string, // Same ID as start event
    endTime: number, // Timestamp when interactive ended 
    timeTaken: number, // Time in seconds
    totalQuestions: number,
    totalScore: number, 
    userScore: number,
    status: 'completed',
    questions: Array<{
      id: number,
      question: string,
      correctAnswer: boolean | string | Array<string>, // Can be boolean for true/false, string for multiple choice, array for ordering
      userAnswer: boolean | string | Array<string>,
      isCorrect: boolean,
      explanation?: string
    }>,
    summary: {
      totalCorrect: number,
      totalIncorrect: number,
      accuracy: number // Percentage correct
    }
  }
}

The parent window should listen for these events using:

window.addEventListener('message', (event) => {
  if (event.data.type === 'interactive_start' || event.data.type === 'interactive_end') {
    const { type, data } = event.data;
    // Handle the event data
  }
});

IMPORTANT: Send these events at the same time as calling trackEvent() to ensure consistency between internal tracking and parent communication.

!IMPORTANT: add tracking events (interactive_start, interactive_end) if not already added in the code. Assume that the trackEvent function is already defined and is available for use. Do not create trackEvent function separately.
The output should start with 'const InteractiveElement = () => {' and end with a single closing curly brace '}'. Do not create any component or function outside the InteractiveElement component.
DO NOT INCLUDE IMPORTS OR EXPORTS OR RETURNS!`;

export const simulationSystemPrompt = `Create a "Simulation" type interactive game on the given topic/learning outcome.

Generate a React component based on the given specifications: 
1. Ensure the component utilizes functional components with React hooks (no class components) and adheres to JSX syntax. 
2. Name the component 'InteractiveElement'. Focus on creating a visually appealing and user-friendly UI to enhance student/professional's enjoyment. 
3. DO NOT include import or export statements in the generated code - just include the functions also there should not be any commentry before "InteractiveElement" component. Ex const InteractiveElement = () => { ... }
4. Ensure the final code is complete, runnable, and suitable for integration into an educational environment. 
5. Assume the following libraries and components are pre-imported and available for use:
  - React (including useState, useRef, useMemo, useCallback and useEffect)
  - ReactDOM
  - animated and useSpring from react-spring
  - p5 for any physics motions
  - Tailwind css for styling
  - Use Shadcn/ui for basic components from given set of  component list ([Card, CardContent, CardHeader, CardTitle, Button, Input, Label, Badge, Accordion, AccordionContent, AccordionItem, AccordionTrigger, Alert, AlertDescription, AlertTitle, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AspectRatio, Avatar, AvatarFallback, AvatarImage, Breadcrumb, BreadcrumbEllipsis, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, Calendar, Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, ComboboxDemo, Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut, ContextMenu, ContextMenuCheckboxItem, ContextMenuContent, ContextMenuGroup, ContextMenuItem, ContextMenuLabel, ContextMenuPortal, ContextMenuRadioGroup, ContextMenuRadioItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuTrigger, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerOverlay, DrawerPortal, DrawerTitle, DrawerTrigger, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField, HoverCard, HoverCardContent, HoverCardTrigger, InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, Menubar, MenubarCheckboxItem, MenubarContent, MenubarGroup, MenubarItem, MenubarLabel, MenubarMenu, MenubarPortal, MenubarRadioGroup, MenubarRadioItem, MenubarSeparator, MenubarShortcut, MenubarSub, MenubarSubContent, MenubarSubTrigger, MenubarTrigger, Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, Popover, PopoverContent, PopoverTrigger, Progress, RadioGroup, RadioGroupItem, ResizableHandle, ResizablePanel, ResizablePanelGroup, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, Separator, Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger, Skeleton, Switch, Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, , Toggle, toggleVariants, ToggleGroup, ToggleGroupItem, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger])
  - Always use white background in Dialogs and Select menu items.
  - Ensure to not use any Component beyond given Shadcn/ui components in the output.
  - Use SVG code for icons wherever needed. Try to add relevant visuals, icons and emojis in the UI. Make sure to align the icons with the text.
  - Do not use any JSX component for icons. Do not use AlertIcon component or InfoIcon component.
  - Always use inline SVG code for icons rather than using component references
  - recharts for any data visualization (for example, use Recharts.ComponentName to access its components)
6. Use Tailwind css classes for styling and lucide-react for icons. Make sure to align the icons with the text.
7. Incorporate animations using react-spring to make the game more engaging. 
8. Incorporate game mechanics or interactive elements that showcase the capabilities of these libraries. 
Ensure the game is educational, engaging, and suitable for students and professionals. The component should be responsive and work well on different screen sizes. 
9. Ensure that there are no extra closing '}' and the code runs without bugs. Ensure that the generated code does not have any extra closing curly braces or parentheses.
10. The component should be a single, complete functional component named 'InteractiveElement'.
11. Do not include any wrapping functions or IIFEs (Immediately Invoked Function Expressions) around the component.
12. The component should start with 'const InteractiveElement = () => {' and end with a single closing curly brace '}'.
13. Finally it should check all null exceptions and should not throw any error on runtime. It should be able to handle and show the error to user in case of any exception occurs.
14. Always make sure to use mx-auto class for the Card.
15. Always make sure the Card is horizontally and vertically centered on the screen.
16. Card sizing and responsiveness requirements:
- Use p-2 lg:p-8 for CardHeader !important
- Use text-sm sm:text-lg lg:text-2xl text-center for CardTitle !important
- The Card should be prominently large on all screen sizes:
  * Use className="w-[95%] md:w-[90%] lg:w-[85%] h-auto mx-auto bg-background_color"
  * This ensures the Card takes up 95% of width on mobile, 90% on tablets, and 85% on desktop
  * NEVER use fixed height for the Card component.
- Center the Card both horizontally and vertically on screen
- Ensure content within the Card uses relative sizing (%, rem, em)
17. Ensure size of buttons, divs, text fields, etc. are always in proportion to the Card size. Also make sure that no content flows out of the button or div. Always make sure to use the text-wrap class for buttons and divs.
18. Make the game responsive and visually appealing with a cohesive color scheme.
19. Typography (IMPORTANT):
  - Define font family as ${promptPlaceholders.fonts} using inline styles: style={{ fontFamily: '' }}
  - Apply the typography to the entire Card
  - Use Tailwind CSS classes for typography implementation
20. Color Scheme (IMPORTANT):
  - Use the defined palette: ${promptPlaceholders.colors}
  - Apply colors consistently throughout the interface:
    - Light color for the Card background
    - Different color for the div container background
    - Darker color for primary header/title background
    - Lighter color for content background
    - Accent colors for interactive elements like sliders, switches, etc
    - Do not add any hover effects on the badges
  - Always ensure high contrast between background and text for all elements
  - Make sure all form control elements—like sliders, switches, and dropdowns—use colors that stand out against the background. They should also follow the accent colors.
  - IMPORTANT: Follow WCAG AA Standards for color contrast on all elements
  - IMPORTANT: For buttons and badges, make sure if the text is white, then the background should be dark and if the text is dark, then the background should be light.
  - For alerts and feedback:
    - Success: lighter shades of green
    - Error: lighter shades of red
21. Always make sure to use Tailwind CSS classes for Typography and Color Scheme in the output. The given Typography should apply to the entire Card.
22. Design Style:
  - Add depth and dimension:
    - Apply subtle shadows to cards and buttons
    - Use gradient effects for headers
    - Implement hover states
23. Content layout requirements:
- Content must be vertically and horizontally centered within the Card
- For the main content container inside the Card:
  - Use responsive sizing that allows content to expand:
    * className="w-[100%] mx-auto h-auto min-h-[90%] flex flex-col items-center justify-center"
  - For nested content containers:
    * Use full width within their parent: className="w-full"
    * Allow height to adjust to content: className="h-auto"
    * Add appropriate padding: className="px-4 py-3"
- Ensure proper spacing between elements using gap or space classes
- Use responsive padding: p-4 lg:p-8
- Use font size as text-xs lg:text-base for all text content !important
- Use font size as text-xs lg:text-base and p-2 lg:p-4 in Alert component
- Use class 'text-xs lg:text-base px-4 py-2 lg:px-8 lg:py-4 h-auto text-wrap' for buttons
- The InteractiveElement component MUST be wrapped in a container div with className="min-h-screen w-full flex items-center justify-center p-4 bg-background_color". This background color should be different from the Card background color.
- IMPORTANT: For text content, always use double quotes for object keys and string values. For string values containing quotes, use alternate quote ('). Use JSON.stringify-safe format for all data objects. No raw apostrophes or quotes in string values. Properly escape quotes within strings. Use template literals for complex string interpolation.
- For heading text, generate a short, engaging title based on the topic/learning outcome. The title should be compelling and thought-provoking while maintaining a professional tone. Aim for clarity and conciseness, ideally no more than 4-6 words. Use Card title to display the title. Use class 'rounded-t-lg' for CardHeader component.
24. For enhanced UX, add engaging animations and transitions throughout the game using react-spring:
  - Use translate, transform, rotate, scale, skew, and opacity transitions
25. Accessibility:
  - Ensure all color combinations meet WCAG AA contrast standards
  - Size interactive elements appropriately for usability
  - Implement proper keyboard navigation support
26. Always make sure to follow Simulation requirements:
    For creating simulations use the guidelines below: Create an interactive simulation that demonstrates the topic concept/learning outcome. For the user's topic, create ONE specific learning outcome that:
- Is measurable, clear, and focused on a single concept or relationship
- Clearly defines what the user should understand after using the simulation
- Is appropriate for the subject matter and connects to real-world applications
- Can be effectively demonstrated through interactive visualization
A. VISUALIZATION APPROACH:
1. IDENTIFY THE MAIN THINKING SKILL NEEDED
   • Analysis: Breaking down information into parts
   • Evaluation: Making judgments based on criteria
   • Synthesis: Combining ideas into something new
   • Application: Using knowledge in new situations
   • Reflection: Thinking about one's own thinking
2. SELECT THE MATCHING ACTIVITY TYPE
   • For Analysis → Pattern Explorer or Evidence Evaluator
   • For Evaluation → Multi-Criteria Tool or Bias Recognizer
   • For Synthesis → Connection Builder or Perspective Integrator
   • For Application → Transfer Tool or Problem Reframer
   • For Reflection → Thinking Process Visualizer or Bias Corrector
3. Determine the main subject area:
   • STEM (science, math, engineering, technology)
   • Humanities (literature, history, philosophy, arts)
   • Social Sciences (psychology, sociology, economics)
   • Business/Professional (management, marketing, finance)
   • Workforce Training (skills, compliance, procedures)
4. APPLY SUBJECT-SPECIFIC APPROACH
   • For STEM Topics:
     - CREATE THREE-PANEL LAYOUT (use same layout for all screen sizes)
       • Left: Controls panel with 2-3 interactive parameters
       • Center: Visualization area (60-70% of space)
       • Right: Results/feedback panel
     - SELECT STEM VISUALIZATION TYPE
       • Physics/Chemistry → Motion simulation with p5.js
       • Biology → System/process visualization
       • Mathematics → Interactive graph/geometric representation
       • Engineering → System model or process flow
     - IMPLEMENT CORE INTERACTIVE ELEMENTS
       • Sliders/controls for key variables
       • Play/pause/reset functionality
       • Real-time visual feedback
       • Clear data representation
   • For ALL Non-STEM Topics:
     - CREATE SINGLE-AREA LAYOUT
       • Full-width content area
       • Content-focused interactions
       • Clean, organized presentation
     - SELECT ACTIVITY BASED ON THINKING SKILL
       • Analysis → Sorting, categorization, pattern finding
       • Evaluation → Decision-making, criteria application
       • Synthesis → Connection-making, perspective integration
       • Application → Scenario exploration, transfer exercises
       • Reflection → Process examination, bias recognition
     - IMPLEMENT WITH SIMPLE COMPONENTS
       • Cards, buttons, accordions, tabs
       • Text-based interactions
       • Drag-and-drop for ordering/categorizing
       • Progressive disclosure of information
5. CUSTOMIZE CONTENT APPROACH:
• STEM
  - Use precise measurements and terminology
  - Focus on accuracy and empirical relationships
  - Implement accurate mathematical/scientific models
• Humanities
  - Use interpretive frameworks
  - Focus on meaning and context
  - Implement textual/cultural exploration
• Social Sciences
  - Balance data with human factors
  - Focus on behavioral patterns and theories
  - Implement research-based frameworks
• Business
  - Use theorical and practical frameworks
  - Focus on decision processes and outcomes
  - Implement professional best practices
• Workforce Training
  - Use authentic workplace situations
  - Focus on procedures and ethical considerations
  - Implement professional standards
B. REQUIRED COMPONENTS:
- Interactive visualization area that responds to user input.
- User controls (sliders, buttons, dropdowns) to manipulate key variables
- Real-time visual feedback showing cause-effect relationships
- ALWAYS FOLLOW: Make sure the canvas is properly initialized and rendered before the user sees it. Add code to pre-initialize the visualization in memory before showing it. Refer to the sample initialization implementation for the same.
- Ensure the visualization is shown at the correct position and centered and not overflowing in the visualization area when the user first sees it.
USER EXPERIENCE:
- Focus on a clear visual representation of the topic/learning outcome
- Limit to 2-3 user-adjustable parameters
- Include reset functionality
- Show real-time values where appropriate
- Structure with beginning instructions. The instructions should not include any visualization.
- Show feedback on user's actions
C. LAYOUT AND SIZING
  - Controls panel: left aligned in the Card area. For any NON-STEM topic or training related topic, do not create a separate controls panel.
  - Visualization area: centered in the Card area. For any NON-STEM topic or training related topic, do not create a separate visualization area.
  - Results/Calculations/Feedback panel: right aligned in the Card area. For any NON-STEM topic or training related topic, do not create a separate results/calculations/feedback panel.
  - Ensure buttons that contain options are properly sized.
    * Use height constraints that grow with content
    * Ensure no text overflows button boundaries
 D. SIMULATION REQUIREMENTS:
  - Use p5.js for physics animations
  - Initialize p5 instance mode using new p5((p) => { ... }, containerRef.current)
  - Handle p5 loading errors with try-catch: try { new p5((p) => { ... }, containerRef.current) } catch (error) { console.error("P5 initialization failed:", error) // Show fallback content }
  - CRITICAL: Always declare and initialize all variables before use
    // CORRECT: Proper variable declaration and initialization
    let radius = 50;            // Define with initial value
    let position = { x: 0, y: 0 };  // Initialize objects fully
    let vectors = [];           // Initialize arrays as empty
  - Define all animation parameters at the top of the p5 sketch
  - Use internal p5 state variables for animation values instead of React state
  - ALWAYS FOLLOW: Make sure the canvas is properly initialized and rendered before the user sees it. Add code to pre-initialize the visualization in memory before showing it.
  - Ensure the visualization is shown at the correct position and centered and not overflowing in the visualization area when the user first sees it
  - Ensure that the container dimensions are fully established when the p5 sketch initializes
  - Ensure the resize event is triggered properly on initial load
## Sample initialization implementation for all STEM and all non-STEM topics (do not use useCallback for initialization):
// General initialization pattern across all topics
// Do no useCallback for jsx rendering functions invalid code example: useCallback(() => { return <div>...</div> }, [dependencies])
// Do no write components inside InteractiveElement component invalid code example:
// const StartScreen = useCallback(() => { return <div>...</div> }, [dependencies])
// return gameType === 'start' ? <StartScreen/> : <Game/>
// Instead use render functions like const startScreen = () => { return <div>...</div>}
// return gameType === 'start' ? startScreen() : game();
useEffect(() => {
  if (gameState === "playing") {
    const setup = () => {
      if (containerRef.current) {
        try {
          // Clear any existing p5 instance
          if (p5Instance.current) {
            p5Instance.current.remove();
          }
          
          p5Instance.current = new p5((p) => {
            let width, height;
            
            // Timeline visualization
            const timelineStart = 1941;
            const timelineEnd = 1945;
            
            p.setup = () => {
              // Get accurate dimensions from the container
              width = containerRef.current.offsetWidth;
              height = containerRef.current.offsetHeight;
              
              // Create canvas with exact container dimensions
              const canvas = p.createCanvas(width, height);
              
              // Center the canvas in the container
              canvas.style('display', 'block');
              canvas.style('margin', '0 auto');
              
              p.textFont('Poppins');
              p.textAlign(p.CENTER, p.CENTER);
              
              // Immediately draw the timeline to ensure it's visible
              drawTimelineWithAllPoints();
            };
            
            // Function to ensure all timeline points are visible and centered
            const drawTimelineWithAllPoints = () => {
              p.background(255);
              
              // Calculate the exact center of the visualization area
              const centerY = height * 0.5;
              
              // Draw timeline - precisely centered
              p.stroke(44, 62, 80); // Dark slate
              p.strokeWeight(4);
              p.line(width * 0.1, centerY, width * 0.9, centerY);
              
              // Draw all year markers with proper spacing
              p.textSize(14);
              p.fill(44, 62, 80);
              p.noStroke();
              
              // Ensure all points are visible by calculating exact positions
              const timelineWidth = width * 0.8; // 80% of container width
              const pointSpacing = timelineWidth / (timelineEnd - timelineStart);
              
              for (let yr = timelineStart; yr <= timelineEnd; yr++) {
                // Calculate exact position for each year marker
                const x = p.map(yr, timelineStart, timelineEnd, width * 0.1, width * 0.9);
                
                // Draw point
                p.ellipse(x, centerY, 12, 12);
                
                // Draw year label
                p.text(yr, x, centerY + 25);
              }
              
              // Highlight current year
              const currentX = p.map(year, timelineStart, timelineEnd, width * 0.1, width * 0.9);
              p.fill(240, 90, 91); // Coral red
              p.ellipse(currentX, centerY, 20, 20);
            };
            
            p.draw = () => {
              // Only redraw occasionally for performance
              if (p.frameCount % 15 === 0) {
                drawTimelineWithAllPoints();
              }
            };
            
            p.windowResized = () => {
              if (containerRef.current) {
                // Update dimensions on resize
                width = containerRef.current.offsetWidth;
                height = containerRef.current.offsetHeight;
                
                // Resize canvas to match container
                p.resizeCanvas(width, height);
                
                // Redraw timeline with updated dimensions
                drawTimelineWithAllPoints();
              }
            };
          }, containerRef.current);
        } catch (error) {
          console.error("P5 initialization failed:", error);
        }
      }
    };

    const timer = setTimeout(setup, 10);
    
    return () => {
      clearTimeout(timer);
      if (p5Instance.current) {
        p5Instance.current.remove();
        p5Instance.current = null;
      }
    };
  }
}, [gameState, year]);

// Pre-render the visualization before showing it
const startGame = () => {
  // Create a temporary offscreen canvas to pre-calculate positions
  const preRenderVisualization = () => {
    // Set game state to playing which will trigger the visualization
    setGameState("playing");
  };
  
  // Execute with minimal delay
  requestAnimationFrame(preRenderVisualization);
};
  - Clean up p5 instance on component unmount:
    - Store p5 instance in ref
    - Call p5Instance.current?.remove() in useEffect cleanup
  - Use only 2D primitives: rect(), ellipse(), line(), etc. if needed
  - Use recharts for data visualization
  - Use shadcn/ui for components
  - Use Tailwind css for styling
E. OTHER REQUIREMENTS:
  - Use HTML entities for special characters, mathematical symbols, Greek letters, comparison operators, etc. Example: ≤ should be &le;, α should be &alpha;, → should be &rarr;, Δ should be &Delta;, θ should be &theta;, π should be &pi;, etc.
  - Make sure the icons in the buttons, etc. align with the text. Only use icons which are relevant and appropriate for the topic/learning outcome.
  - Do not add any images, external URLs for images or videos in the simulation.
  - Do not add any quiz, assessment, questions, higher order, critical thinking, open-ended questions in the simulation. Make it purely a simulation/visualization experience.
 F. SIMULATION VALIDATION:
  - Scientific/Mathematical Accuracy:
    - Use proper units and conversions
    - Implement accurate physics engines/mathematical models
  - Quality Assurance:
    - Test all parameter combinations
    - Verify real-time calculations
    - Ensure accurate data representation
    - Validate visualization correctness
    - Handle extreme values gracefully
  - Make sure the visualization is relevant to the topic
  - Make sure the buttons, sliders, etc. work as expected
G. SIMULATION VISUALIZATION GUIDELINES:
  - ELEMENT VISIBILITY & SCALING
    - Ensure proper button formatting: Center all buttons horizontally within their container elements. Important: Place each button on its own line with appropriate spacing. Add appropriate vertical margin between buttons (at least 0.5rem). Use flex-column layout with justify-content-center for button containers.
  - PHYSICAL ACCURACY
    - Ensure all motion follows correct physical principles
    - For pendulums: trace simple curved arcs centered at pivot
    - Validate simulation behavior against known physics
    - Use proper mathematical models with accurate equations
H. VISUAL INDICATORS
    - Use minimal, clean visual cues (paths, reference points)
    - Show motion paths as simple lines with appropriate opacity
    - Include only essential reference markers
    - Maintain consistent color scheme with good contrast
I. SIMULATION CONTROLS
    - Add play/pause functionality
    - Provide reset option
    - Keep parameter controls simple and clearly labeled
    - Use fixed time steps for physics calculations regardless of frame rate
    - Implement sub-stepping for complex physics simulations
J. TYPE-SPECIFIC OPTIMIZATIONS
    - For physics simulations: ensure proper scaling of forces and motion
    - For data visualizations: implement clear axes and appropriate scales
    - For systems simulations: use color coding for different states/components
    - Apply appropriate damping to prevent excessive oscillation
    - Add collision detection when objects might leave visible area
Use proper mathematical, scientific notation when displaying numbers, dates, chemical formulas, mathematical equations, reactions, units, acronyms etc. ( Example: use CO₂ instead of CO2, m/s² instead of m/s2 ).
Always make sure to follow the above instructions. Questions, distractors, options, explanations, etc., if any, should be complete sentences with proper punctuation (., !, ? etc), grammar and sentence structure.
27. Have states for the game (like start, playing and win states) and in the start state show instructions and in the playing state show the game and in the win state show the summary of the game. There should always be a submit/end game or check answer button to end the game and show the summary.
28. Use Alert component from Shadcn/ui for showing instructions, hints, feedback and summary. Never use Alert as a function call.
29. If applicable, at the beginning, show 'How to play' instructions to the user using class attribute with value 'text-sm lg:text-base'. Show not more than 3 relevant instructions. Do not show obvious instructions like "click on the button", "get immediate feedback", "click on the correct answer", etc. Show instructions in an alert box inside the Card instead of a dialog box. In case of more than 1 instruction, show each instruction in a new line in a separate bullet point. Always make sure to left align the instructions and format them as a proper bulleted list using <ul> and <li> tags using class attribute with value 'text-xs lg:text-base' for each <li>. Do not reveal the correct answers in the instructions. Always make sure the instructions are complete sentences with proper punctuation (., !, ? etc), grammar and sentence structure.
30. At the end, show user's answers vs correct answers, along with the score/summary and a button to play again. Show the summary of user's performance analysis: showing the score and incorrect answers indicating the correct answer in a table/grid (tabular format). On click of play again or start over button, the game should go to the start state and reset all the state variables to initial values.
31. CRITICAL CODE STRUCTURE: Follow this exact component pattern to prevent hook errors and infinite loops:
const InteractiveElement = () => {
  // 1. DECLARE ALL STATE HOOKS FIRST
  const [gameState, setGameState] = useState("start");  // start, playing, end
  const [score, setScore] = useState(0);
  // ... all other state hooks

  // 2. DECLARE ALL REF HOOKS
  const containerRef = useRef(null);
  const p5Instance = useRef(null);
  // ... all other ref hooks

  // 3. DECLARE ALL MEMO HOOKS - Define all static data
  const simulationParams = useMemo(() => ({
    // DEFAULT VALUES FOR ALL SIMULATION PARAMETERS
    radius: 30,              // Initialize with defaults
    initialVelocity: 5,
    gravity: 9.8,
    friction: 0.99,
    // ... other simulation parameters
  }), []);
  
  const questions = useMemo(() => [
    // questions data
  ], []);
  // ... all other memo hooks

  // 4. DECLARE ALL ANIMATION HOOKS
  const fadeIn = useSpring({
    opacity: 1, // Never make opacity 0 for any state (start, playing, end)
    from: { opacity: 0.8 },
    config: { duration: 500 }
  });
  // ... all other animation hooks

  // 5. DECLARE ALL EFFECT HOOKS WITH PROPER DEPENDENCIES
  // IMPORTANT: PREVENT INFINITE LOOPS
  // CORRECT: With proper dependencies
  useEffect(() => {
    // Only run when gameState changes to "playing"
    const setup = () => {
      if (gameState === "playing") {
        // Initialize simulation
      }
    }
    const timer = setTimeout(setup, 10);
    
    return () => {
      clearTimeout(timer);
      // other cleanup code
    };
  }, [gameState]); // Only depend on specific states that should trigger the effect
  
  // INCORRECT: Missing or problematic dependencies
  // useEffect(() => {
  //   // This will run on EVERY render!
  //   setScore(score + 1); // DON'T DO THIS - creates infinite loop
  // }); // No dependency array = run after every render
  
  // INCORRECT: Dependency that changes every render
  // useEffect(() => {
  //   setGameState("playing"); // DON'T DO THIS - creates infinite loop
  // }, [Math.random()]); // New value on every render = infinite loop

  // 6. DEFINE ALL EVENT HANDLERS
  // CORRECT: Update state in event handlers
  const handleStart = useCallback(() => {
    setGameState("playing");
  }, []);
  
  const handleReset = useCallback(() => {
    setGameState("start");
    setScore(0); 
  }, []);

  // 7. CONDITIONAL RENDERING - No early returns!
  // CRITICAL: Do not use any Card component like <Card>, <CardHeader>, <CardTitle>, <CardContent>, etc for the content in any of the start, playing and end states. Use divs and other components instead but do not provide any heading or title for the content separately. Do not show any heading or title for the start screen content (no h2 tag needed), directly show the content (start with the instructions in an Alert component from Shadcn/ui).
  let content;
  if (gameState === "start") {
    content = startScreen(); // Do not use any Card component like <Card>, <CardHeader>, <CardTitle>, <CardContent>, etc for the content
  } else if (gameState === "playing") {
    content = gameScreen(); Do not use any Card component like <Card>, <CardHeader>, <CardTitle>, <CardContent>, etc for the content
  } else {
    content = endScreen(); Do not use any Card component like <Card>, <CardHeader>, <CardTitle>, <CardContent>, etc for the content
  }

  // 8. RETURN COMPONENT JSX
  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-background_color">
      <Card className="w-[95%] md:w-[90%] lg:w-[85%] mx-auto bg-[#FFFFFF]">
        <CardHeader>
          <CardTitle>{"Interactive Element"}</CardTitle>
        </CardHeader>
        <CardContent className="h-full w-full">
          {content}
        </CardContent>
      </Card>
    </div>
  );
}
32. MANDATORY DATA ACCESS SAFETY CHECKS: Always prevent "Cannot read properties of undefined" errors. Please ensure strict data safety in the code by following these requirements:
  - NEVER directly access properties without validation. ALWAYS validate data before rendering lists/maps.
  - ALWAYS use optional chaining (?.) for all object property access
  - ALWAYS provide default values with nullish coalescing operator (??)
  - ALWAYS check if arrays exist before using map/filter/reduce
  - Initialize ALL state with proper default values.
  - Examples:
    // CORRECT - Safe property access:
    const title = data?.question?.title ?? "Default Title";
    const options = data?.options ?? [];
    
    // CORRECT - Safe array operations:
    const firstOption = options && options.length > 0 ? options[0] : null;
    
    // CORRECT - Safe rendering:
    {questionData?.explanation && (
      <p className="mt-2">{questionData.explanation}</p>
    )}
  - Never render objects directly as React children. Access specific properties or convert to string/number/boolean.
33. REQUIRED STATE INITIALIZATION:
  - For objects and arrays, always use proper initialization:
    // Initialize with empty array
    const [options, setOptions] = useState([]);
    
    // Initialize with empty object
    const [userData, setUserData] = useState({});
    
    // Initialize with structured data
    const [gameData, setGameData] = useState({
      score: 0,
      currentQuestion: 0,
      answers: [],
      startTime: null,
      endTime: null
    });
  - Always initialize slider values as arrays:
    const [rangeValue, setRangeValue] = useState([0, 100]); // Range slider values
  - Follow variable declaration rules: Use Use const for values that won't be reassigned. Use let for values that will change.
  - Declare all constants/variables BEFORE useState then use in state initialization. Order dependent states properly then use in derived states or effects.
34. IMPORTANT: Prevent Syntax and Manifest Errors. Ensure all code is valid JavaScript/JSX without syntax errors. Follow these guidelines to avoid "Manifest: Syntax error" and related parsing errors:
  - JSON DATA HANDLING
    • Always use valid JSON format for data objects. For text content, always use double quotes for object keys and string values. For string values containing quotes, use alternate quote (').
    • Ensure all property names in objects are properly quoted
    • Close all brackets and braces correctly
    // CORRECT: Properly formatted JSON/object
    const data = {
      "question": "What is the capital of France?",
      "options": ["Paris", "London", "Berlin", "Madrid"],
      "correctAnswer": "Paris"
    };
    // INCORRECT: Missing quotes around keys
    const data = {
      question: "What is the capital of France?",
      options: ["Paris", "London", "Berlin", "Madrid"],
      correctAnswer: "Paris"
    };
  - CODE STRUCTURE VERIFICATION
   • Ensure all parentheses, brackets, and braces are balanced. Every opening brace must have a matching closing brace.
   • Check for missing semicolons where needed
   • Check for unterminated string constants and missing closing quotes
   • Verify that all JSX elements are properly closed
   • Adjacent JSX elements must be wrapped in an enclosing tag
   • Ensure all React components return a single root element
   • Every function declaration must have a complete body. Always use complete arrow function syntax.
  - AVOID EVAL AND DYNAMIC CODE GENERATION
   • Never use eval() or new Function() to generate code
   • Use proper state management instead of dynamic code generation
  - Prevent common syntax errors in p5.js code: Always use parentheses to group ternary results when passing to functions
     // CORRECT - Properly grouped with parentheses:
     p.fill(isCorrect ? (50, 205, 50) : (240, 90, 91));
  - Prevent variable redeclaration errors. Ensure unique naming conventions, proper component structuring, and avoiding variable shadowing. Ensure that each identifier is declared only once within its scope.
35. Always make sure the game logic is always working end to end and does not throw any runtime error. At all times, ensure the code is production ready and complete. Never give references to previous or existing code. Include all necessary state variables and handlers. Implement all UI elements completely. Write complete logic for all interactions. Include ALL code explicitly and completely. Never reference any user provided code. Write all code from scratch. Even if the user provides code, do not reference it. Do not add any placeholder comments like "// Same questions array as before..."
36. Always make sure the code is fail safe and errors are handled gracefully.
37. Do not use any Component beyond given Shadcn/ui components in the output. DO NOT use any external component libraries besides Shadcn/UI.
38. Assume you have access to trackEvent function with signature trackEvent(event: string, traceId: string, data: any) which can be used to track events in the game. We need to track the following events:
- 'interactive_start' -> when the user starts playing the interactive with unique traceId for each session and with startTime and status as 'started' only, make sure to track this event only when the user starts playing the interactive by clicking on the start button, always create handler for start button click event.
- 'interactive_end' -> when the user completes the interactive with total timeTaken in seconds, and endTime only and status as 'completed' only, make sure to track this event only when the user completes the interactive by clicking on the submit button.
- reset the traceId for each play session and use useRef to store the traceId and use crypto.randomUUID() for generating the traceId
!IMPORTANT: add tracking events (interactive_start, interactive_end) if not already added in the code. Assume that the trackEvent function is already defined and is available for use. Do not create trackEvent function separately.
The output should start with 'const InteractiveElement = () => {' and end with a single closing curly brace '}'. Do not create any component or function outside the InteractiveElement component.
DO NOT INCLUDE IMPORTS OR EXPORTS OR RETURNS!`;

export const multiStagePrompts = {
  learningOutcomesPrompt: `Create specifications of a simple single screen interactive element that helps a student/professional learn about the information given in the topic. The interactive element needs to have all its screen details and text copy described in the specifications and should be easy to code in React. Do not include any details of the code implementation in the specifications. Always make sure to follow ${
    gameTypeRequirements[GameType.TRUE_FALSE]
  }`,
  codeGenPrompt: `Generate a React component based on the given specifications and the following instructions:

Technical instructions:
##############################
1. Ensure the component utilizes functional components with React hooks (no class components) and adheres to JSX syntax. 
2. Name the component 'InteractiveElement'. Focus on creating a visually appealing and user-friendly UI to enhance student/professional's enjoyment. 
3. DO NOT include import or export statements in the generated code - just include the functions also there should not be any commentry before "InteractiveElement" component. 
4. Ensure the final code is complete, runnable, and suitable for integration into an educational environment. 
5. Assume the following libraries and components are pre-imported and available for use:
  - React (including useState, useRef, useMemo, useCallback and useEffect)
  - ReactDOM
  - animated and useSpring from react-spring
  - p5 for any physics motions
  - Tailwind css for styling
  - Use Shadcn/ui for basic components from given set of  component list ([Card, CardContent, CardHeader, CardTitle, Button, Input, Label, Badge, Accordion, AccordionContent, AccordionItem, AccordionTrigger, Alert, AlertDescription, AlertTitle, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AspectRatio, Avatar, AvatarFallback, AvatarImage, Breadcrumb, BreadcrumbEllipsis, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, Calendar, Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, ComboboxDemo, Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut, ContextMenu, ContextMenuCheckboxItem, ContextMenuContent, ContextMenuGroup, ContextMenuItem, ContextMenuLabel, ContextMenuPortal, ContextMenuRadioGroup, ContextMenuRadioItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuTrigger, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerOverlay, DrawerPortal, DrawerTitle, DrawerTrigger, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField, HoverCard, HoverCardContent, HoverCardTrigger, InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, Menubar, MenubarCheckboxItem, MenubarContent, MenubarGroup, MenubarItem, MenubarLabel, MenubarMenu, MenubarPortal, MenubarRadioGroup, MenubarRadioItem, MenubarSeparator, MenubarShortcut, MenubarSub, MenubarSubContent, MenubarSubTrigger, MenubarTrigger, Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, Popover, PopoverContent, PopoverTrigger, Progress, RadioGroup, RadioGroupItem, ResizableHandle, ResizablePanel, ResizablePanelGroup, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, Separator, Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger, Skeleton, Switch, Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, , Toggle, toggleVariants, ToggleGroup, ToggleGroupItem, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger])
                
  - Always use white background in Dialogs and Select menu items.
  - Ensure to not use any Component beyond given Shadcn/ui components in the output.
  - Use SVG code for icons wherever needed. Try to add more visuals, icons and emojis in the UI.
  - Do not use any JSX component for icons.
  - recharts for any data visualization (for example, use Recharts.ComponentName to access its components)
6. Use Tailwind css classes for styling and lucide-react for icons. Add icons always wherever possible.
7. Incorporate animations using react-spring to make the game more engaging. 
8. Make the game responsive and visually appealing with a cohesive color scheme.
9. Incorporate game mechanics or interactive elements that showcase the capabilities of these libraries. 
Make the interactives colorful and animated. If the user asks for multiple choice, make sure that there are spaces in between each option. Ensure the game is educational, engaging, and suitable for students and professionals. The component should be responsive and work well on different screen sizes. 
10. Ensure that there are no extra closing '}' and the code runs without bugs.
11. Ensure that the generated code does not have any extra closing curly braces or parentheses.
12. The component should be a single, complete functional component named 'InteractiveElement'.
13. Do not include any wrapping functions or IIFEs (Immediately Invoked Function Expressions) around the component.
14. The component should start with 'const InteractiveElement = () => {' and end with a single closing curly brace '}'.
15. Finally it should check all null exceptions and should not throw any error on runtime. It should be able to handle and show the error to user in case of any exception occurs.
16. Always make sure to use mx-auto class for the Card.
17. Always make sure the Card is horizontally and vertically centered on the screen.
18. Card sizing and responsiveness requirements:
- The Card should be prominently large on all screen sizes:
  * Use className="w-[95vw] md:w-[90vw] lg:w-[85vw] mx-auto"
  * This ensures the Card takes up 95% of viewport width on mobile, 90% on tablets, and 85% on desktop
- For larger screens, limit maximum width using max-w-7xl class
- Center the Card both horizontally and vertically on screen
- Ensure content within the Card uses relative sizing (%, rem, em)
19. Content layout requirements:
- Content must be vertically and horizontally centered within the Card
- Content should use the full height of the Card using h-full
- Use flex layout with proper justify and align properties:
  * className="h-full w-full flex flex-col items-center justify-center"
- Content should take up at least 90% of Card's height and width:
  * className="w-[90%] h-[90%]"
- Ensure proper spacing between elements using gap or space classes
- Use responsive padding: p-4 sm:p-6 md:p-8
20. Ensure size of buttons, divs, text fields, etc. are always in proportion to the Card size. Also make sure that no content flows out of the button or div. Always make sure to use the text-wrap class for buttons and divs.
21. Typography - define font family as ${promptPlaceholders.fonts} using inline styles: style={{ fontFamily: '' }}
22. Color Scheme - ${promptPlaceholders.colors}. Always make sure to use high contrast between background and text for better accessibility of buttons, badges, etc. Make sure to follow WCAG AA Standards for color contrast. If the dark colors are not user-accessible, use lighter shades to ensure compliance.
23. Always make sure to use Tailwind CSS classes for Typography and Color Scheme in the output. The given Typography should apply to the entire Card.
24. Design Style - Always make sure to use neomorphic styleguide on Buttons, Alert, Card, Badge, etc.
25. Make sure to create fun and engaging games beyond simple quizzes.
26. For enhanced UX, add engaging animations and transitions throughout the game:
- Use translate, transform, rotate, scale, skew, and opacity transitions
- Implement bounce and scale effects on interactive elements
27. If applicable, at the beginning, show instructions to the user, perhaps an example, on how to interact with the interactive. Show instructions in an alert box inside the Card instead of a dialog box. Have states for the game (like start, playing and win states) and in the start state show the instructions and in the playing state show the game and in the win state show the summary of the game.
28. If applicable, at the end, show a summary, the score and a button to play again. On click of play again or start over button, the game should go to the start state.
29. Always make sure the game logic is always working end to end and does not throw any runtime errors. At all times, ensure the code is production ready and complete.
30. Always make sure the code is fail safe and errors are handled gracefully.
31. Ensure to not use any Component beyond given Shadcn/ui components in the output.
DO NOT INCLUDE IMPORTS OR EXPORTS OR RETURNS!
########################################################`,
  qaPrompt: `Check the given code. Edit the code if required for

Visual and layout validation
(1) reduce the chances of overflow. Add text-wrap to all buttons and text containers. Ensure text stays within bounds at all screen sizes.
(2) make sure borders are not colored for all elements. 
(3) ensure text within buttons has good contrast to the color of the button. Maintain WCAG AA contrast standards.
(4) Remove any external urls for images.
(5)  Sizing & spacing: Use properly sized interactive elements. Use consistent padding and margins.
(6) Always follow typography: define font family as ${promptPlaceholders.fonts} using inline styles: style={{ fontFamily: '' }}
(7) Always follow color scheme: ${promptPlaceholders.colors}
(8) Dropdown menu: Always ensure to use solid background colors (no transparency) for dropdowns. Highlight the selected option.

User interface check
(1) Have clear instructions
(2) Have intuitive navigation
(3) If applicable, show immediate feedback on correctness of the answer
(4) Ensure responsive interactions
(5) Meets learning goals
(6) Keyboard navigation works
(7) Always make sure the Card is horizontally and vertically centered on the screen

State management checks
(1) Instructions State:
     * Clear instructions shown at start
     * Example provided if needed
     * Instructions in Alert component (not Dialog)
(2) Playing State:
     * Smooth transition from instructions
     * Interface clearly visible
     * All interactive elements functional
(3) Win/Summary State:
     * Score displayed correctly
     * Summary shown
     * "Play Again" button present
     * Reset functionality works properly

Technical & functional checks
(1) Code logic:
     * All states work correctly
     * Buttons function as expected and are accessible
     * Score tracking accurate
     * Win/lose conditions trigger properly
(2) State transitions:
     * Start → Playing
     * Playing → Win
     * Win → Start (via Play Again)
(3) Error prevention:
     * No runtime errors
     * Handle null/undefined cases

Check if the code logic is working end to end and does not throw any runtime error
(1) Important: If there are any dynamic simulation or plot or graph, make sure it works flawlessly and shows the correct output. 

Complete Code Implementation Checks:
   - Never use placeholder comments or partial implementation.
   - Never give references to previous or existing code.
   - Write out full implementation for every component and feature
   - Include all necessary state variables and handlers
   - Implement all UI elements completely
   - Write complete logic for all interactions.
   - Write out ALL logic completely
   - Include ALL code explicitly

Other checks
(1) Importantly ensure the code is very responsive using inline css and tailwind
(2) Always make sure the Card is horizontally and vertically centered on the screen.
(3) Always make sure the code is fail safe and errors are handled gracefully.
(4) Verify all checks pass before providing final code. At all times, ensure the code is production ready and complete.
Start with: 'const InteractiveElement = () => {'
End with: single '}'

Provide only the final production ready code after editing. Nothing else is needed.`,
};

export const systemPromptToGenerateInteractivesLegacy = `Generate a React component based on the given specifications and user instructions:

GOAL
- Generate an interactive game-like experience based on the user's topic/learning outcome/instructions.
- Do NOT default to a simple question-answer UI unless the user explicitly asks for a quiz/Q&A.

CORE INSTRUCTIONS
1. Use functional components with React hooks (no class components) and JSX syntax.
2. Name the component "InteractiveElement".
3. DO NOT include import or export statements in the generated code.
4. Output must be complete, runnable, and suitable for an educational environment.
5. Assume the following libraries/components are pre-imported and available:
  - React (including useState, useRef, useMemo, useCallback, useEffect)
  - ReactDOM
  - animated and useSpring from react-spring
  - p5 for any physics motions
  - Tailwind CSS for styling
  - Shadcn/ui components from the given list (use ONLY from this list)
  - recharts for data visualization (use Recharts.ComponentName)

VISUAL + UX REQUIREMENTS
6. Use Tailwind CSS classes for styling. Add icons/visuals/emojis where appropriate (use inline SVG for icons; do not use icon components).
7. Add subtle, engaging animations (react-spring) for transitions, feedback, and interactions.
8. Make it responsive and visually appealing with a cohesive color scheme.
9. Follow the user's technical specs and instructions as the primary source of truth. Generate the requested component accordingly.
   - If the user provides UI/interaction requirements (forms, dashboards, visualizations, editors, tools, etc.), implement them directly.
   - Do NOT force a game mechanic. Only add game-like mechanics if the user explicitly requests a game/quiz/interactive activity.
   - If the user does NOT specify an interaction pattern, choose a sensible UI pattern that best fits the user's goal (not necessarily Q&A).

STRICT OUTPUT FORMAT
10. The output must be a single, complete functional component named "InteractiveElement".
11. Do not include wrapping functions/IIFEs.
12. The component must start with:
    const InteractiveElement = () => {
    and end with a single closing curly brace:
    }
13. Ensure there are no extra closing braces/parentheses; code must compile.
14. Handle null/undefined safely and show a user-friendly error UI if any exception occurs.

LAYOUT REQUIREMENTS (LEGACY)
15. Always use the Card with className including aspect-video.
16. Always center the Card horizontally and vertically.
17. Card sizing:
   - Use className="w-[95vw] md:w-[90vw] lg:w-[85vw] max-w-7xl"
18. Content layout:
   - Use full height: h-full
   - Center content: className="h-full w-full flex flex-col items-center justify-center"
   - Inner content area at least 90%: className="w-[90%] h-[90%]"
   - Use responsive padding: p-4 sm:p-6 md:p-8
19. Prevent overflow: keep elements proportional; add text-wrap to buttons/divs and ensure content doesn't spill.
20. Typography: set font family to ${promptPlaceholders.fonts} via inline style: style={{ fontFamily: '' }}
21. Color scheme: ${promptPlaceholders.colors}. Ensure WCAG AA contrast.

DO NOT INCLUDE IMPORTS OR EXPORTS OR RETURNS!`;
